import { useCallback, useEffect, useState, type FunctionComponent } from "react";
import { CaretRightOutlined, CloseOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";

import {
  CHALLENGE_COMMENT_MEDIA_THUMB_HEIGHT,
  CHALLENGE_MEDIA_THUMB_HEIGHT,
  ChallengePostMediaType,
  challengeMediaAspectRatio,
  type ChallengePostMedia,
} from "@fitness-app/data-models/entities/ChallengeFeed";

type Props = {
  media: ChallengePostMedia[];
  /** `comment` shrinks the thumbnails — a reply's attachments must not outweigh the reply. */
  variant?: "post" | "comment";
};

/**
 * TRE-1730 — a post's attachments on the board: ONE horizontally scrollable row of thumbnails.
 *
 * Replaces two things that made the board unusable:
 *
 * - `w-full max-h-[420px]` on the image and the `<video>`. Forcing the width while capping the
 *   height strands a portrait upload (a phone screenshot, a screen recording) as a small frame in
 *   a card-wide void. Fixing the row's HEIGHT instead makes each thumbnail only as wide as its own
 *   clamped shape needs, and the row scrolls rather than growing;
 * - the preview `Modal width={880}` with a `w-full` image inside, which turned a portrait
 *   screenshot into ~1900px of modal the reviewer had to SCROLL. The lightbox below fits the frame
 *   to the viewport instead, and it plays videos too — they had no preview at all before.
 */
export const ChallengeMediaGallery: FunctionComponent<Props> = ({ media, variant = "post" }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (!media.length) {
    return null;
  }

  const height = variant === "comment" ? CHALLENGE_COMMENT_MEDIA_THUMB_HEIGHT : CHALLENGE_MEDIA_THUMB_HEIGHT;

  return (
    <>
      <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
        {media.map((item, index) => (
          <MediaThumb key={item.url} item={item} height={height} onOpen={() => setOpenIndex(index)} />
        ))}
      </div>

      <ChallengeMediaLightbox media={media} openIndex={openIndex} onOpenIndexChange={setOpenIndex} />
    </>
  );
};

const MediaThumb: FunctionComponent<{
  item: ChallengePostMedia;
  height: number;
  onOpen: () => void;
}> = ({ item, height, onOpen }) => {
  const isVideo = item.type === ChallengePostMediaType.Video;
  // A video with no generated thumbnail has nothing to show — a dark tile behind the play badge
  // reads as "video" far better than a broken-image frame would.
  const poster = isVideo ? item.thumbnailUrl : item.url;

  return (
    <button
      type="button"
      aria-label={isVideo ? "Odtwórz wideo" : "Zobacz zdjęcie"}
      onClick={onOpen}
      style={{ height, width: height * challengeMediaAspectRatio(item) }}
      className="relative shrink-0 cursor-zoom-in overflow-hidden rounded-lg border border-[#f0f0f0] bg-[#fafafa] p-0 transition hover:brightness-95"
    >
      {poster ? (
        // Top-anchored: a screenshot is recognised by its top, not by the band that happens to sit
        // in the middle of the crop.
        <img src={poster} alt="" loading="lazy" className="h-full w-full object-cover object-top" />
      ) : null}

      {isVideo ? (
        <span className="absolute inset-0 flex items-center justify-center bg-black/25">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white">
            <CaretRightOutlined />
          </span>
        </span>
      ) : null}
    </button>
  );
};

/** Full-viewport preview. Fits the frame to the screen — it must never be something you scroll. */
const ChallengeMediaLightbox: FunctionComponent<{
  media: ChallengePostMedia[];
  openIndex: number | null;
  onOpenIndexChange: (index: number | null) => void;
}> = ({ media, openIndex, onOpenIndexChange }) => {
  const current = openIndex === null ? null : media[openIndex];

  const step = useCallback(
    (direction: 1 | -1) => {
      if (openIndex === null || media.length < 2) {
        return;
      }
      onOpenIndexChange((openIndex + direction + media.length) % media.length);
    },
    [media.length, onOpenIndexChange, openIndex],
  );

  useEffect(() => {
    if (openIndex === null) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onOpenIndexChange(null);
      }
      if (event.key === "ArrowRight") {
        step(1);
      }
      if (event.key === "ArrowLeft") {
        step(-1);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onOpenIndexChange, openIndex, step]);

  if (!current) {
    return null;
  }

  return (
    // Above antd's Drawer (1000) — the thread drawer opens this from inside itself.
    <div
      role="presentation"
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/90"
      onClick={() => onOpenIndexChange(null)}
    >
      {current.type === ChallengePostMediaType.Image ? (
        <img
          src={current.url}
          alt=""
          className="max-h-[92vh] max-w-[92vw] rounded-lg object-contain"
          onClick={(event) => event.stopPropagation()}
        />
      ) : (
        <video
          src={current.url}
          controls
          autoPlay
          className="max-h-[92vh] max-w-[92vw] rounded-lg bg-black"
          onClick={(event) => event.stopPropagation()}
        />
      )}

      {media.length > 1 ? (
        <>
          <button
            type="button"
            aria-label="Poprzedni załącznik"
            className="absolute left-4 rounded-full border-0 bg-white/10 p-3 text-white hover:bg-white/20"
            onClick={(event) => {
              event.stopPropagation();
              step(-1);
            }}
          >
            <LeftOutlined />
          </button>
          <button
            type="button"
            aria-label="Następny załącznik"
            className="absolute right-4 rounded-full border-0 bg-white/10 p-3 text-white hover:bg-white/20"
            onClick={(event) => {
              event.stopPropagation();
              step(1);
            }}
          >
            <RightOutlined />
          </button>
          <span className="absolute bottom-6 rounded-full bg-white/10 px-3 py-1 text-sm text-white">
            {(openIndex ?? 0) + 1}/{media.length}
          </span>
        </>
      ) : null}

      <button
        type="button"
        aria-label="Zamknij podgląd"
        className="absolute right-4 top-4 rounded-full border-0 bg-white/10 p-2 text-white hover:bg-white/20"
        onClick={() => onOpenIndexChange(null)}
      >
        <CloseOutlined />
      </button>
    </div>
  );
};
